import React, { memo, forwardRef, useImperativeHandle, useState, useCallback, useEffect, useMemo } from "react";
import Modal from "@mui/material/Modal";
import PropTypes from "prop-types";

import api from "services/api";
import COMMON from "common";
import { formatDatePattern } from "common/calendar";
import { formatTimeValue } from "common/time";
import { formatCurrency } from "common/format-currency-pattern";
import serveRequestErrors, { axiosCancelled } from "common/serve-request-errors";
import { getRecommendedReplacementLeaveByTime } from "common/overtime";
import AppStatus from "components/app-status";
import AppButton from "components/app-button";
import AppCloseIcon from "components/icons/app-close-icon";
import pdfIcon from "assets/images/pdf-icon.svg";
import rejectIcon from "assets/images/components/pages/leave-management/reject-icon.svg";
import checkedIcon from "assets/images/components/pages/leave-management/checked-icon.svg";

export const AppOvertimeModal = (props, ref) => {
	const [data, setData] = useState({});
	const [visible, setVisible] = useState(false);
	const { approveRef, rejectReasonRef, showActions } = props;

	const onHandleDismiss = useCallback(() => {
		setData({});
		setVisible(false);
	}, []);

	const onHandleShow = useCallback((id) => {
		setData((prev) => ({ ...prev, id }));
		setVisible(true);
	}, []);

	//prettier-ignore
	const onHandleApprove = useCallback(() => {
		approveRef?.current.onHandleShow(data);
		onHandleDismiss();
	}, [data, approveRef, onHandleDismiss]);

	//prettier-ignore
	const onHandleReject = useCallback(() => {
		rejectReasonRef?.current.onHandleShow(data.id);
		onHandleDismiss();
	}, [data.id, rejectReasonRef, onHandleDismiss]);

	const overtimeInfo = useMemo(() => {
		const isRejected = data?.status === COMMON.STATUS_ID.REJECTED;

		const info = [
			{ label: "Employee Name", value: data.employeeName || "-" },
			{ label: "Overtime Date", value: data.overtimeDate ? formatDatePattern(data.overtimeDate) : "-" },
			{ label: "Start Time", value: formatTimeValue(data.startTime) || "-" },
			{ label: "End Time", value: formatTimeValue(data.endTime) || "-" },
			{ label: "Overtime Hours", value: data.overtimeHours ?? "-" },
			{ label: "Replacement Leave (Days)", value: data.replacementLeave ?? "-" },
			{ label: "Expiry Date", value: data.expiryLeaveDate ? formatDatePattern(data.expiryLeaveDate) : "-" },
			{ label: "Allowance (RM)", value: data.allowanceAmount == null ? "-" : formatCurrency(data.allowanceAmount) },
			{ label: "Submit Date", value: data.createdDate ? formatDatePattern(data.createdDate) : "-" },
			{ label: "Attachment", value: data?.files?.[0] || "-" },
			{ label: "Description", value: data.description || "-" },
		];

		if (isRejected) {
			info.push({ label: "Reject Reason", value: data.rejectReason || "-" });
		}

		info.push({ label: "Remarks", value: data.remarks || "-" });

		return info;
	}, [data]);

	const recommendedReplacementLeave = useMemo(() => getRecommendedReplacementLeaveByTime(data.startTime, data.endTime), [data.startTime, data.endTime]);

	//prettier-ignore
	const actionButtons = useMemo(() => {
		if (!showActions || data.status !== COMMON.STATUS_ID.PENDING_ADMIN) return [];

		return [
			{ label: "Reject", outline: true, icon: rejectIcon, onClick: onHandleReject },
			{ label: "Approve", icon: checkedIcon, onClick: onHandleApprove },
		];
	}, [showActions, data.status, onHandleReject, onHandleApprove]);

	//prettier-ignore
	useImperativeHandle(ref, () => ({
		onHandleShow: onHandleShow,
		onHandleDismiss: onHandleDismiss,
	}));

	useEffect(() => {
		const onHandleGetDetails = async () => {
			let response = null;

			try {
				/* fileURL is a short lived presigned link, so always read it from a fresh fetch */
				response = await api.get.overtime.details(data.id);
			} catch (error) {
				// the record may be gone by the time it is opened, so close rather than
				// leave an empty modal behind the error toast
				if (!axiosCancelled(serveRequestErrors(error))) onHandleDismiss();
			}

			if (response) {
				setData((prev) => ({ ...prev, ...response }));
			}
		};

		if (data.id) onHandleGetDetails();
	}, [data.id, onHandleDismiss]);

	return (
		<Modal classes={{ root: "app-overtime-modal" }} open={visible} onClose={onHandleDismiss} aria-labelledby="overtime-modal" aria-describedby="overtime-modal">
			<div className="overtime-modal">
				<div className="overtime-modal__main">
					<button className="overtime-modal__close-button" onClick={onHandleDismiss}>
						<AppCloseIcon color="#666666" />
					</button>

					<div className="overtime-modal__header">
						<p className="overtime-modal__title">Overtime Details</p>
					</div>

					<div className="info">
						<div className="info__box">
							<p className="overtime-modal__text">Recommended Replacement Leave</p>
							<p className="overtime-modal__title">{data.startTime ? recommendedReplacementLeave : "-"}</p>
						</div>
						<div className="info__box">
							<p className="overtime-modal__text">Status</p>
							<div className="overtime-modal__title">
								<AppStatus status={data?.status || "-"} description={data?.statusDescription} />
							</div>
						</div>
					</div>

					<div className="overtime-modal__body">
						<div className="overtime-modal__container">
							<div className="overtime-modal__wrapper">
								{overtimeInfo.map((o, i) => {
									const fileName = o.value?.fileName;

									return (
										<div className="overtime-modal__info" key={i}>
											<p className="overtime-modal__label">{o.label}</p>
											{!fileName && <p className="overtime-modal__value">{o.value}</p>}
											{fileName && (
												<p className="overtime-modal__value">
													<a href={o.value.fileURL} className="overtime-modal__link" target="_blank" rel="noopener noreferrer">
														<img src={pdfIcon} alt="attachment" /> {fileName}
													</a>
												</p>
											)}
										</div>
									);
								})}
							</div>

							<div className="overtime-modal__button-container">
								{actionButtons.map((btn, i) => (
									<AppButton key={i} type="button" outline={btn.outline} icon={btn.icon} label={btn.label} onClick={btn.onClick} />
								))}
							</div>
						</div>
					</div>
				</div>
			</div>
		</Modal>
	);
};

export default memo(forwardRef(AppOvertimeModal));

AppOvertimeModal.propTypes = {
	ref: PropTypes.object,
	approveRef: PropTypes.object,
	rejectReasonRef: PropTypes.object,
	showActions: PropTypes.bool,
};
